import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Grid, IconButton, InputAdornment } from '@mui/material';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { api } from 'common';
import { C2_FONT } from '../components/Table/C2UserTable';
import {
  DemoModeAlert,
  SettingToggle,
  SettingsCard,
  SettingsField,
  SettingsNotice,
  SettingsSaveBar,
  isEmail,
} from '../components/settings/C2SettingsKit';

const fromStore = (smtpdata) => ({
  fromEmail: smtpdata?.fromEmail || '',
  host: smtpdata?.smtpDetails?.host || '',
  port: smtpdata?.smtpDetails?.port ? String(smtpdata.smtpDetails.port) : '',
  user: smtpdata?.smtpDetails?.auth?.user || '',
  pass: smtpdata?.smtpDetails?.auth?.pass || '',
  secure: !!smtpdata?.smtpDetails?.secure,
});

const validate = (d) => {
  const e = {};
  if (!isEmail(d.fromEmail)) e.fromEmail = 'Correo no válido.';
  if (!/^[A-Za-z0-9.-]+$/.test(d.host.trim())) e.host = 'Servidor no válido, por ejemplo smtp.gmail.com';
  const port = Number(d.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) e.port = 'Puerto entre 1 y 65535.';
  if (!d.user.trim()) e.user = 'Obligatorio.';
  if (!d.pass) e.pass = 'Obligatorio.';
  return e;
};

const SERVER_ERRORS = {
  forbidden: 'Tu sesión no tiene permisos de administrador para cambiar el correo.',
  invalid_smtp_details: 'El servidor rechazó los datos: revisa correo, servidor, puerto, usuario y contraseña.',
};

const SmtpSettings = ({ onDirtyChange }) => {
  const { checkSMTP } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const smtpdata = useSelector((state) => state.smtpdata);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const source = useMemo(() => fromStore(smtpdata), [smtpdata]);
  const [data, setData] = useState(source);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setData(source);
  }, [source]);

  const dirty = JSON.stringify(data) !== JSON.stringify(source);
  const errors = useMemo(() => validate(data), [data]);
  const shownErrors = touched ? errors : {};

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const setField = (name, value) => {
    setTouched(true);
    setData((d) => ({ ...d, [name]: value }));
  };

  const handleSave = async () => {
    if (!canEdit) {
      setNotice({ severity: 'info', message: 'Modo demostración: los cambios no se guardan.' });
      return;
    }
    setTouched(true);
    if (Object.keys(errors).length) {
      setNotice({ severity: 'error', message: 'Completa los campos marcados en rojo.' });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const res = await checkSMTP(data.fromEmail.trim(), {
        host: data.host.trim(),
        port: Number(data.port),
        secure: data.secure,
        auth: { user: data.user.trim(), pass: data.pass },
      });
      if (res && res.success) {
        setNotice({ severity: 'success', message: `Conexión correcta. Enviamos un correo de prueba a ${data.fromEmail.trim()} y se guardó la configuración.` });
      } else {
        const code = res && res.error;
        setNotice({ severity: 'error', message: SERVER_ERRORS[code] || `No se pudo enviar el correo de prueba; no se guardó nada. Detalle: ${code || 'sin respuesta'}` });
      }
    } catch (e) {
      setNotice({ severity: 'error', message: `No se pudo contactar el servidor: ${e?.message || e}` });
    } finally {
      setSaving(false);
    }
  };

  const hidden = !canEdit;
  const portNum = Number(data.port);
  const portHint = data.secure && portNum === 587
    ? 'El puerto 587 normalmente usa conexión segura apagada (STARTTLS).'
    : !data.secure && portNum === 465
      ? 'El puerto 465 normalmente requiere conexión segura (SSL) encendida.'
      : null;

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      {!canEdit ? <DemoModeAlert /> : null}
      <SettingsNotice notice={notice} onClose={() => setNotice(null)} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SettingsCard icon={AlternateEmailOutlinedIcon} title="Remitente" subtitle="Correo desde el que la app envía recibos, avisos y recuperación de contraseña.">
            <SettingsField
              name="fromEmail"
              label="Correo remitente"
              type="email"
              value={hidden ? 'Oculto en modo demo' : data.fromEmail}
              onChange={setField}
              error={shownErrors.fromEmail}
              disabled={hidden}
              placeholder="no-responder@conecta2.com"
            />
            <Alert severity="info" icon={false} sx={{ mt: 1, borderRadius: '12px', fontFamily: C2_FONT }}>
              Al guardar enviamos un correo de prueba a esta dirección. La configuración solo se guarda si el envío funciona.
            </Alert>
          </SettingsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SettingsCard icon={DnsOutlinedIcon} title="Servidor SMTP" subtitle="Datos que te entrega tu proveedor de correo.">
            <Grid container spacing={1.5}>
              <Grid item xs={8}>
                <SettingsField name="host" label="Servidor" value={hidden ? 'Oculto' : data.host} onChange={setField} error={shownErrors.host} disabled={hidden} placeholder="smtp.gmail.com" />
              </Grid>
              <Grid item xs={4}>
                <SettingsField name="port" label="Puerto" value={hidden ? '' : data.port} onChange={setField} error={shownErrors.port} disabled={hidden} placeholder="465" inputProps={{ inputMode: 'numeric' }} />
              </Grid>
            </Grid>
            <SettingsField name="user" label="Usuario" value={hidden ? 'Oculto' : data.user} onChange={setField} error={shownErrors.user} disabled={hidden} autoComplete="off" />
            <SettingsField
              name="pass"
              label="Contraseña"
              type={showPass && !hidden ? 'text' : 'password'}
              value={hidden ? 'oculto' : data.pass}
              onChange={setField}
              error={shownErrors.pass}
              disabled={hidden}
              autoComplete="new-password"
              helper="En Gmail usa una contraseña de aplicación, no la de tu cuenta."
              InputProps={{
                endAdornment: hidden ? null : (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass((s) => !s)} edge="end" aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                      {showPass ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <SettingToggle
              name="secure"
              label="Conexión segura (SSL)"
              description="Enciéndelo para el puerto 465. Para 587 déjalo apagado."
              checked={data.secure}
              onChange={setField}
              disabled={hidden}
            />
            {portHint ? <Alert severity="warning" sx={{ mt: 1, borderRadius: '12px', fontFamily: C2_FONT }}>{portHint}</Alert> : null}
          </SettingsCard>
        </Grid>
      </Grid>
      <SettingsSaveBar
        dirty={dirty}
        saving={saving}
        onSave={handleSave}
        onDiscard={() => { setData(source); setTouched(false); }}
        disabled={!canEdit}
        saveLabel="Probar y guardar"
        alwaysEnabled
      />
    </form>
  );
};

export default SmtpSettings;
